export type EnhancedLineType = 'header' | 'bullet' | 'paragraph';

export interface EnhancedLine {
  type: EnhancedLineType;
  text: string;
}

const BULLET_PATTERN = /^\s*[-*•]\s+/;
const HEADING_HASHES_PATTERN = /^#+\s*/;
const TRAILING_COLON_PATTERN = /:\s*$/;
/** Numbered list items, e.g. `1. `, `10. `, `1) `. */
const NUMBERED_LIST_PATTERN = /^\s*\d+[.)]\s+/;
/**
 * `Label: value` lines (e.g. "Constitutional: Febrile",
 * "Vitals: Slight fever"). The colon must be followed by at least one
 * non-whitespace character so trailing-colon section headers like
 * "Subjective:" still pass the header heuristic.
 */
const LABELED_PARAGRAPH_PATTERN = /^[^:#\n]+:\s+\S/;
/** Inline emphasis markers — `**bold**`, `*italic*`, `_underline_`, etc. */
const INLINE_EMPHASIS_PATTERN = /[*_`]/;
/**
 * Lines wholly wrapped in `**...**` (no asterisks inside). The seeded
 * onboarding-v2 specialty notes use this convention for section titles
 * (e.g. `**Plan**`, `**Pre-anaesthetic assessment**`), so we promote them
 * to headers even though they contain emphasis markers.
 */
const BOLD_ONLY_PATTERN = /^\*\*([^*]+)\*\*$/;
/** Lines longer than this without `#` are treated as paragraphs, not headers. */
const HEADER_MAX_LENGTH = 40;
const SENTENCE_TERMINATOR_PATTERN = /[.!?]$/;

function looksLikeHeader(text: string): boolean {
  // Anything explicitly marked with `#` is always a header.
  if (text.startsWith('#')) return true;
  // Numbered list items belong to their parent section as paragraphs —
  // never promote them to headers, even when they look like one
  // (e.g. "2. Symptomatic management" with no trailing period).
  if (NUMBERED_LIST_PATTERN.test(text)) return false;
  // `Label: value` lines (e.g. "Constitutional: Febrile",
  // "**Chief Complaint:** Persistent back pain") are content belonging to
  // their parent section, not new section headers — even when the label is
  // wrapped in bold.
  if (LABELED_PARAGRAPH_PATTERN.test(text)) return false;
  const cleaned = text.replace(TRAILING_COLON_PATTERN, '').trim();
  if (!cleaned) return false;
  // A line that is *entirely* a bold span is a section header in the
  // seeded specialty notes — promote it before the inline-emphasis
  // disqualifier kicks in.
  if (BOLD_ONLY_PATTERN.test(cleaned)) return true;
  if (cleaned.length > HEADER_MAX_LENGTH) return false;
  if (INLINE_EMPHASIS_PATTERN.test(cleaned)) return false;
  if (SENTENCE_TERMINATOR_PATTERN.test(cleaned)) return false;
  return true;
}

function parseLine(line: string): EnhancedLine | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  if (BULLET_PATTERN.test(trimmed)) {
    return { type: 'bullet', text: trimmed.replace(BULLET_PATTERN, '').trim() };
  }

  if (looksLikeHeader(trimmed)) {
    let headerText = trimmed.replace(HEADING_HASHES_PATTERN, '').trim();
    // Strip surrounding `**` from bold-only headers (`**Plan**` → `Plan`).
    const boldMatch = headerText.match(BOLD_ONLY_PATTERN);
    if (boldMatch?.[1]) headerText = boldMatch[1].trim();
    headerText = headerText.replace(TRAILING_COLON_PATTERN, '').trim();
    if (!headerText) return null;
    return { type: 'header', text: headerText };
  }

  return { type: 'paragraph', text: trimmed };
}

/**
 * Groups parsed lines into sections, where each section starts with a
 * header (or the first non-header line, if the note has no headers).
 */
export function parseExampleNoteIntoSections(noteText: string): EnhancedLine[][] {
  if (!noteText) return [];

  const lines = noteText
    .split('\n')
    .map(parseLine)
    .filter((line): line is EnhancedLine => line !== null);

  if (lines.length === 0) return [];

  const sections: EnhancedLine[][] = [];
  let current: EnhancedLine[] = [];

  for (const line of lines) {
    if (line.type === 'header' && current.length > 0) {
      sections.push(current);
      current = [];
    }
    current.push(line);
  }

  if (current.length > 0) sections.push(current);
  return sections;
}
